/********************************************************************************
 * Copyright (c) 2026 CrossBreeze, EclipseSource and others.
 *
 * This program and the accompanying materials are made available under the
 * terms of the MIT License which is available in the project root.
 *
 * SPDX-License-Identifier: MIT
 ********************************************************************************/

import {
   type Action,
   type ElementAndRoutingPoints,
   type GModelElement,
   GLSPHiddenBoundsUpdater,
   type LocalRequestBoundsAction,
   RequestBoundsAction,
   ServerAction,
   isRoutable
} from '@eclipse-glsp/client';
import { type Clock, type Logger, SystemClock } from '@hydranium/protocol';
import { inject, injectable, optional } from 'inversify';
import { type VNode } from 'snabbdom';
import { HydraniumClientTypes } from './types';

/** How many scoped element ids the log line names before summarising the rest.
 *  A scoped request names one element in practice; the cap only stops a
 *  pathological caller from writing a thousand ids into the log. */
const SCOPE_ID_CAP = 3;

/**
 * Instruments sprotty's hidden-bounds pass.
 *
 * GLSP 2.7: it also drops short routes before they go on the wire, see
 * {@link dropShortRoute}.
 *
 * **Why the framework owns this.** Every GLSP head runs the hidden-bounds pass on
 * every model update, and it is the one client-side phase that scales with the
 * *rendered element count* rather than the model size — labels, compartments and
 * edge decorations expand a modest node count into orders of magnitude more DOM
 * elements. Without a timing line there is no way to tell a slow measurement pass
 * apart from slow edge routing or a slow server round trip; with one,
 * `bounds-request` either exonerates the client in a sentence or names the
 * dominant cost.
 *
 * Emits exactly one `trace` line per bounds request — `trace` because the
 * frequency matches the per-action traffic `HydraniumGlspActionDispatcher` logs
 * there (a bounds request is driven by those actions), so it belongs with them
 * rather than above them. The line carries:
 *
 *  - `server` / `local` — whether the request came from the server or from a
 *    client-local {@link LocalRequestBoundsAction}
 *  - `scope=<ids>` — the elements a scoped request named; absent when the request
 *    covers the whole diagram
 *  - `measured=N` — DOM elements whose bounds were read. Exceeds the scoped id
 *    count because the base class expands each requested element to its
 *    descendants
 *  - `getBBox=Nms` — {@link getBoundsFromDOM} **only**: the layout pass and the
 *    `ComputedBoundsAction` assembly stay outside it, so the number isolates raw
 *    measurement cost
 *
 * Composed from {@link Clock.measure} rather than `Tracer.time`, matching
 * `HydraniumDocumentBuilder`'s phase instrumentation. `Tracer.time` is for "tell
 * me when this is slow" and is deliberately silent on a fast success, which would
 * drop the element count on every normal pass — and forcing it to emit costs a
 * paired `[#N start]` line per request. This is a per-occurrence record, so it
 * measures and composes its own line.
 *
 * Bound by `createGlspClientModule` via `rebind(GLSPHiddenBoundsUpdater)`.
 */
@injectable()
export class HydraniumHiddenBoundsUpdater extends GLSPHiddenBoundsUpdater {
   @inject(HydraniumClientTypes.Logger) protected readonly hydraniumLogger!: Logger;

   /** The diagram container's {@link Clock}, or a `SystemClock`; it only measures. */
   @inject(HydraniumClientTypes.Clock) @optional() protected readonly clock: Clock = new SystemClock();

   /**
    * Scope description for the in-flight request, read from the `cause` in
    * {@link postUpdate} and consumed by {@link getBoundsFromDOM} — which the base
    * class calls with no arguments, so the cause cannot be threaded through.
    */
   protected currentScope?: string;

   // GLSP 2.7: overridden only to drop a short route.
   override decorate(vnode: VNode, element: GModelElement): VNode {
      super.decorate(vnode, element);
      if (isRoutable(element)) {
         this.dropShortRoute();
      }
      return vnode;
   }

   /**
    * GLSP 2.7: drop the routing entry the base class just pushed when it
    * carries fewer than two points, as 2.8 does itself. A 2.7 server rejects
    * the whole action over such a route, and the diagram stays blank.
    *
    * A router returns fewer than two points for an edge it cannot route, as
    * sprotty's polyline router does when the edge's source or target is not in
    * the model. The server keeps the edge's last route until a later pass
    * routes it.
    */
   protected dropShortRoute(): void {
      const lastRoute = this.element2route.at(-1);
      if (lastRoute !== undefined && !this.hasValidRoute(lastRoute)) {
         this.element2route.pop();
      }
   }

   /** GLSP 2.7: a route needs at least a start and an end point to describe an edge. */
   protected hasValidRoute(route: ElementAndRoutingPoints): boolean {
      return route.newRoutingPoints !== undefined && route.newRoutingPoints.length >= 2;
   }

   protected override getBoundsFromDOM(): void {
      if (this.currentScope === undefined) {
         // Not inside an instrumented `postUpdate` (a subclass calling directly);
         // with no cause there is nothing to attribute a measurement to.
         super.getBoundsFromDOM();
         return;
      }
      // Read before the pass: the base class clears the map on its way out.
      const measured = this.getElement2BoundsData().size;
      const { elapsedMs } = this.clock.measure(() => super.getBoundsFromDOM());
      this.hydraniumLogger.trace(this.formatBoundsRequest(this.currentScope, measured, elapsedMs));
   }

   /** Format the per-request line. Override to change the wording. */
   protected formatBoundsRequest(scope: string, measured: number, elapsedMs: number): string {
      return `bounds-request[${scope}] measured=${measured} getBBox=${elapsedMs.toFixed(0)}ms`;
   }

   override postUpdate(cause?: Action): void {
      if (cause?.kind !== RequestBoundsAction.KIND) {
         super.postUpdate(cause);
         return;
      }
      this.currentScope = this.formatCause(cause);
      try {
         super.postUpdate(cause);
      } finally {
         this.currentScope = undefined;
      }
   }

   /**
    * Scope facts for the log line: where the request came from, and which
    * elements it asked for.
    *
    * Origin is read from {@link ServerAction}, the `__receivedFromServer` marker
    * `GLSPModelSource` stamps on inbound actions, rather than from
    * `LocalRequestBoundsAction.is`. The latter would answer the same today, but
    * only incidentally: its `elementIDs` check is declared optional, so it is
    * really just "a bounds request that did not come from the server". Keying on
    * the marker states that directly, and keeps an *unscoped* local request from
    * being mislabelled `server` if GLSP ever tightens that guard.
    */
   protected formatCause(cause: Action): string {
      const origin = ServerAction.is(cause) ? 'server' : 'local';
      const scope = this.formatScope(cause as RequestBoundsAction);
      return scope ? `${origin} scope=${scope}` : origin;
   }

   /**
    * Name the elements a scoped request asked for, or `undefined` when it covers
    * the whole diagram.
    *
    * Deliberately the ids and not their count. A scoped request in practice names
    * a single element, so a count is always `1` and says nothing, while the id
    * says *which* element was re-measured — and it is the only part of the line
    * that distinguishes one such request from the next. It also stops the line
    * reading as a contradiction: `scope=node0, measured=6` is coherent (the base
    * class expands a requested element to its descendants, so one id measures six
    * DOM elements), where `scoped=1, measured=6` invites the reader to wonder
    * which number is wrong.
    *
    * A request naming only the root is treated as unscoped: it measures
    * everything, so the id adds nothing over the absence of a `scope` tag.
    */
   protected formatScope(cause: RequestBoundsAction): string | undefined {
      const scopedIds = (cause as LocalRequestBoundsAction).elementIDs;
      if (!scopedIds || scopedIds.length === 0) {
         return undefined;
      }
      const named = scopedIds.filter(id => id !== cause.newRoot?.id);
      if (named.length === 0) {
         return undefined;
      }
      if (named.length > SCOPE_ID_CAP) {
         return `${named.slice(0, SCOPE_ID_CAP).join(',')},+${named.length - SCOPE_ID_CAP} more`;
      }
      return named.join(',');
   }
}
